import {Badge, HoverCard} from '@mantine/core';
import {Alert} from 'src/design/atoms/Alert';
import {Box} from 'src/design/atoms/Box';
import {Center} from 'src/design/atoms/Center';
import {Collapse} from 'src/design/atoms/Collapse';
import {Group} from 'src/design/atoms/Group';
import {Loader} from 'src/design/atoms/Loader';
import {SimpleGrid} from 'src/design/atoms/SimpleGrid';
import {Stack} from 'src/design/atoms/Stack';
import {Switch} from 'src/design/atoms/Switch';
import {Text} from 'src/design/atoms/Text';
import {UnstyledButton} from 'src/design/atoms/UnstyledButton';
import {
  IconChevronDown,
  IconChevronRight,
  IconInfoCircle,
} from '@tabler/icons-react';
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {ModuleEnum} from 'src/api/ProactApiClient';
import {BulkToggleLinks} from 'src/design/atoms/BulkToggleLinks';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {useGetModulePermissionSchemaQuery} from 'src/redux/queries/userGroupPermissions';
import type {IFormDataV2} from 'src/types/FormV2';
import {
  GetModulePermissionFieldLabel,
  GetModulePermissionFieldName,
} from './userGroupHelpers';

export interface UserGroupModulePermissionsFormProps {
  mode: 'edit' | 'view';
  formData: IFormDataV2;
  onChange: (name: string, value: any, displayValue?: string) => void;
}

export const UserGroupModulePermissionsForm: React.FC<
  UserGroupModulePermissionsFormProps
> = ({mode, formData, onChange}) => {
  const {
    data: schema,
    isLoading: isSchemaLoading,
    isFetching: isSchemaFetching,
  } = useGetModulePermissionSchemaQuery();
  const isLoading = isSchemaLoading || isSchemaFetching;
  const isViewMode = mode === 'view';

  const [expandedModules, setExpandedModules] = useState<Set<string>>(
    new Set()
  );
  const [userHasToggledExpand, setUserHasToggledExpand] = useState(false);

  const toggleModule = useCallback((moduleName: string) => {
    setUserHasToggledExpand(true);
    setExpandedModules(prev => {
      const next = new Set(prev);
      if (next.has(moduleName)) {
        next.delete(moduleName);
      } else {
        next.add(moduleName);
      }
      return next;
    });
  }, []);

  const expandAll = useCallback(() => {
    setUserHasToggledExpand(true);
    if (!schema) return;
    setExpandedModules(new Set(schema.modules.map(m => ModuleEnum[m.module])));
  }, [schema]);

  const collapseAll = useCallback(() => {
    setUserHasToggledExpand(true);
    setExpandedModules(new Set());
  }, []);

  const checkAllGlobal = useCallback(() => {
    if (!schema) return;
    schema.modules.forEach(module => {
      module.operations.forEach(op => {
        const fieldName = GetModulePermissionFieldName(
          module.module,
          op.operation,
          op.operationName
        );
        if (!formData[fieldName]) {
          onChange(fieldName, true);
        }
      });
    });
  }, [schema, formData, onChange]);

  const uncheckAllGlobal = useCallback(() => {
    if (!schema) return;
    schema.modules.forEach(module => {
      module.operations.forEach(op => {
        const fieldName = GetModulePermissionFieldName(
          module.module,
          op.operation,
          op.operationName
        );
        if (formData[fieldName]) {
          onChange(fieldName, false);
        }
      });
    });
  }, [schema, formData, onChange]);

  const moduleStats = useMemo(() => {
    if (!schema) return {};
    const stats: Record<string, {granted: number; total: number}> = {};
    schema.modules.forEach(module => {
      const key = ModuleEnum[module.module];
      let granted = 0;
      module.operations.forEach(op => {
        const fieldName = GetModulePermissionFieldName(
          module.module,
          op.operation,
          op.operationName
        );
        if (formData[fieldName]) granted++;
      });
      stats[key] = {granted, total: module.operations.length};
    });
    return stats;
  }, [schema, formData]);

  // Auto-expand sections that have at least one permission enabled.
  // Keeps running until the user manually interacts with expand/collapse,
  // ensuring late-arriving formData (async parent load) is picked up.
  useEffect(() => {
    if (userHasToggledExpand || !schema) return;
    const modulesWithPermissions = new Set<string>();
    schema.modules.forEach(module => {
      const key = ModuleEnum[module.module];
      const stats = moduleStats[key];
      if (stats && stats.granted > 0) {
        modulesWithPermissions.add(key);
      }
    });
    setExpandedModules(modulesWithPermissions);
  }, [schema, moduleStats, userHasToggledExpand]);

  if (isLoading) {
    return (
      <Center py="xl">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!schema) return null;

  const totalGranted = Object.values(moduleStats).reduce(
    (sum, s) => sum + s.granted,
    0
  );
  const totalOps = Object.values(moduleStats).reduce(
    (sum, s) => sum + s.total,
    0
  );

  return (
    <Stack gap="md" data-testid="module-permissions-form">
      <Alert variant="light" color="blue" icon={<IconInfoCircle size={16} />}>
        <Text size="sm">
          Configure which operations each module allows for this group.
          {!isViewMode &&
            ' Toggle individual operations or expand modules to see available permissions.'}
        </Text>
      </Alert>

      <Group justify="space-between">
        <Badge size="md" variant="light" color="blue">
          {totalGranted} of {totalOps} permissions granted
        </Badge>
        <Group gap="md">
          {!isViewMode && (
            <BulkToggleLinks
              onEnableAll={checkAllGlobal}
              onDisableAll={uncheckAllGlobal}
              testIdPrefix="modules"
            />
          )}
          <Text size="sm" c="dimmed">
            ·
          </Text>
          <Group gap={4}>
            <UnstyledButton
              onClick={expandAll}
              data-testid="expand-all-modules">
              <Text size="sm" c="blue" td="hover:underline">
                Expand All
              </Text>
            </UnstyledButton>
            <Text size="sm" c="dimmed">
              |
            </Text>
            <UnstyledButton
              onClick={collapseAll}
              data-testid="collapse-all-modules">
              <Text size="sm" c="blue" td="hover:underline">
                Collapse All
              </Text>
            </UnstyledButton>
          </Group>
        </Group>
      </Group>

      {schema.modules.map(module => {
        const moduleKey = ModuleEnum[module.module];
        const isExpanded = expandedModules.has(moduleKey);
        const stats = moduleStats[moduleKey] || {granted: 0, total: 0};

        return (
          <SectionCard
            key={moduleKey}
            title={module.name}
            rightSection={
              <Group gap="xs">
                <Badge
                  size="sm"
                  variant="light"
                  color={stats.granted > 0 ? 'green' : 'gray'}>
                  {stats.granted} / {stats.total}
                </Badge>
              </Group>
            }
            data-testid={`module-section-${moduleKey}`}>
            <UnstyledButton
              onClick={() => toggleModule(moduleKey)}
              w="100%"
              p="xs"
              px="md"
              data-testid={`toggle-module-${moduleKey}`}>
              <Group gap="xs">
                {isExpanded ? (
                  <IconChevronDown size={14} />
                ) : (
                  <IconChevronRight size={14} />
                )}
                <Text size="sm" c="dimmed">
                  {isExpanded ? 'Hide' : 'Show'} {stats.total} operation
                  {stats.total !== 1 ? 's' : ''}
                </Text>
              </Group>
            </UnstyledButton>
            <Collapse
              in={isExpanded}
              transitionDuration={userHasToggledExpand ? 200 : 0}>
              <Box p="md" pt={0}>
                {isViewMode ? (
                  <Group gap="xs">
                    {module.operations.map(op => {
                      const fieldName = GetModulePermissionFieldName(
                        module.module,
                        op.operation,
                        op.operationName
                      );
                      const label = GetModulePermissionFieldLabel(op);
                      const isGranted = !!formData[fieldName];
                      return (
                        <Badge
                          key={fieldName}
                          size="md"
                          variant="light"
                          color={isGranted ? 'green' : 'gray'}
                          style={{
                            textTransform: 'none',
                            opacity: isGranted ? 1 : 0.6,
                          }}>
                          {label}
                        </Badge>
                      );
                    })}
                  </Group>
                ) : (
                  <Stack gap="sm">
                    <Group justify="flex-end">
                      <BulkToggleLinks
                        size="xs"
                        onEnableAll={() => {
                          module.operations.forEach(op => {
                            const fn = GetModulePermissionFieldName(
                              module.module,
                              op.operation,
                              op.operationName
                            );
                            if (!formData[fn]) onChange(fn, true);
                          });
                        }}
                        onDisableAll={() => {
                          module.operations.forEach(op => {
                            const fn = GetModulePermissionFieldName(
                              module.module,
                              op.operation,
                              op.operationName
                            );
                            if (formData[fn]) onChange(fn, false);
                          });
                        }}
                        testIdPrefix={`module-${moduleKey}`}
                      />
                    </Group>
                    <SimpleGrid cols={{base: 1, sm: 2, md: 3}} spacing="sm">
                      {module.operations.map(op => {
                        const fieldName = GetModulePermissionFieldName(
                          module.module,
                          op.operation,
                          op.operationName
                        );
                        const label = GetModulePermissionFieldLabel(op);
                        return (
                          <Switch
                            key={fieldName}
                            data-testid={`perm-${fieldName}`}
                            label={
                              op.description ? (
                                <Group gap={4} wrap="nowrap">
                                  <span>{label}</span>
                                  <HoverCard
                                    width={300}
                                    shadow="md"
                                    openDelay={200}
                                    closeDelay={400}
                                    withArrow>
                                    <HoverCard.Target>
                                      <IconInfoCircle
                                        size={14}
                                        style={{
                                          opacity: 0.5,
                                          flexShrink: 0,
                                          cursor: 'help',
                                        }}
                                      />
                                    </HoverCard.Target>
                                    <HoverCard.Dropdown>
                                      <Text size="xs" fw={600} mb={4}>
                                        Description
                                      </Text>
                                      <Text size="sm">{op.description}</Text>
                                    </HoverCard.Dropdown>
                                  </HoverCard>
                                </Group>
                              ) : (
                                label
                              )
                            }
                            color="green.8"
                            checked={!!formData[fieldName]}
                            onChange={() =>
                              onChange(fieldName, !formData[fieldName])
                            }
                          />
                        );
                      })}
                    </SimpleGrid>
                  </Stack>
                )}
              </Box>
            </Collapse>
          </SectionCard>
        );
      })}
    </Stack>
  );
};
